import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Shield, Book, Download, Loader2, CheckCircle } from "lucide-react";
import { API, BACKEND_URL, COVER_IMAGE, SSB_PLACEHOLDER } from "../lib/constants";
import { useAdmin } from "../contexts/AdminContext";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const SERIES_LIST = [
  { slug: "the-red-ink-chronicles", label: "The Red Ink Chronicles" },
  { slug: "signed-sealed-betrayed", label: "Signed, Sealed, Betrayed" },
  { slug: "the-notarys-shadow", label: "The Notary's Shadow" },
];

export default function Episodes() {
  const { slug } = useParams();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return parseInt(sp.get('season')) || 1;
  });
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [allSeriesMeta, setAllSeriesMeta] = useState([]);
  const [downloadState, setDownloadState] = useState({ active: false, progress: 0, total: 0, done: false });
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();

  const seriesSlug = slug || null;

  // If no slug, fetch all series metadata for the picker
  useEffect(() => {
    if (!seriesSlug) {
      const fetchAllSeries = async () => {
        try {
          const res = await axios.get(`${API}/series`);
          setAllSeriesMeta(res.data);
        } catch (e) { console.error(e); }
        setLoading(false);
      };
      fetchAllSeries();
    }
  }, [seriesSlug]);

  useEffect(() => {
    if (!seriesSlug) return;
    const fetchSeriesInfo = async () => {
      try {
        const res = await axios.get(`${API}/series/${seriesSlug}`);
        setSeriesInfo(res.data);
      } catch (e) { console.error(e); }
    };
    fetchSeriesInfo();
  }, [seriesSlug]);

  useEffect(() => {
    if (!seriesSlug) return;
    const fetchEpisodes = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/episodes?series=${seriesSlug}&season=${selectedSeason}&limit=150`);
        setEpisodes(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchEpisodes();
  }, [selectedSeason, seriesSlug]);

  const totalSeasons = seriesInfo?.seasons || 5;
  const seasonNames = seriesInfo?.season_names || {};

  const mixedEpisodes = episodes.filter(ep => ep.mixed_audio_url || ep.audio_url);

  const handleDownloadSeason = async () => {
    if (downloadState.active || mixedEpisodes.length === 0) return;
    setDownloadState({ active: true, progress: 0, total: mixedEpisodes.length, done: false });
    const zip = new JSZip();
    const seriesTitle = (seriesInfo?.title || seriesSlug).replace(/[^a-zA-Z0-9 ]/g, '');
    let completed = 0;
    for (const ep of mixedEpisodes) {
      const audioUrl = ep.mixed_audio_url || ep.audio_url;
      const filename = `S${String(selectedSeason).padStart(2,'0')}E${String(ep.episode_number).padStart(2,'0')} - ${ep.title.replace(/[^a-zA-Z0-9 '-]/g, '')}.mp3`;
      try {
        const resp = await fetch(`${BACKEND_URL}${audioUrl}`);
        if (resp.ok) {
          const blob = await resp.blob();
          zip.file(filename, blob);
        }
      } catch (e) { console.error(`Failed to download ${ep.title}:`, e); }
      completed++;
      setDownloadState(prev => ({ ...prev, progress: completed }));
    }
    const zipBlob = await zip.generateAsync({ type: "blob", compression: "STORE" });
    saveAs(zipBlob, `${seriesTitle} - Season ${selectedSeason}.zip`);
    setDownloadState({ active: false, progress: 0, total: 0, done: true });
    setTimeout(() => setDownloadState(prev => ({ ...prev, done: false })), 4000);
  };

  // Series picker view (no slug)
  if (!seriesSlug) {
    return (
      <div className="min-h-screen pt-24 pb-32 px-6" data-testid="episodes-page">
        <div className="max-w-5xl mx-auto">
          <header className="mb-12 text-center">
            <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-4">ALL SERIES</p>
            <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-4">EPISODES</h1>
            <p className="text-gray-500 text-sm">Choose a series to browse episodes</p>
          </header>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
              {(allSeriesMeta.length > 0 ? allSeriesMeta : SERIES_LIST).map((s) => {
                const seriesMeta = allSeriesMeta.find(m => m.slug === (s.slug || s));
                const title = seriesMeta?.title || seriesMeta?.name || s.label || s.slug;
                const desc = seriesMeta?.description || seriesMeta?.tagline || '';
                const cover = seriesMeta?.cover_image || SSB_PLACEHOLDER;
                const slugVal = s.slug || s;
                return (
                  <button
                    key={slugVal}
                    onClick={() => navigate(`/series/${slugVal}/episodes`)}
                    className="group relative overflow-hidden border border-white/10 rounded-lg hover:border-red-500/40 transition-all text-left bg-[#0a0a0a]"
                    data-testid={`series-pick-${slugVal}`}
                  >
                    <div className="h-44 relative overflow-hidden">
                      <div
                        className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                        style={{ backgroundImage: `url(${cover})` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
                      {seriesMeta?.content_rating === '17+' && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold tracking-wider rounded flex items-center gap-1">
                          <Shield size={10} /> 17+
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-cinzel text-lg font-bold mb-1.5 group-hover:text-red-500 transition-colors">
                        {title}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-3">{desc}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-600 font-mono">
                          {seriesMeta?.seasons || '5'} SEASONS
                        </span>
                        <ChevronRight size={16} className="text-red-500 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Series-specific episodes view
  if (loading && !seriesInfo) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-6" data-testid="episodes-page">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 text-center">
          <button
            onClick={() => navigate('/episodes')}
            className="text-sm text-gray-500 hover:text-red-500 transition-colors mb-4 inline-flex items-center gap-1"
            data-testid="back-to-series-list"
          >
            <ChevronRight size={14} className="rotate-180" />
            All Series
          </button>

          {seriesInfo?.content_rating === '17+' && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-600/80 text-white text-xs font-bold tracking-wider rounded mb-4 ml-3">
              <Shield size={12} /> 17+
            </div>
          )}

          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-4">
            {(seriesInfo?.title || seriesInfo?.name || 'AUDIO DRAMA').toUpperCase()}
          </p>
          <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-6">EPISODES</h1>

          <div className="flex justify-center gap-2 flex-wrap">
            {Array.from({ length: totalSeasons }, (_, i) => i + 1).map(season => (
              <button
                key={season}
                onClick={() => setSelectedSeason(season)}
                className={`px-6 py-3 font-anton text-lg transition-all ${
                  selectedSeason === season
                    ? 'bg-red-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`season-tab-${season}`}
              >
                S{season}
                {seasonNames[String(season)] && (
                  <span className="text-xs font-manrope ml-2 opacity-70">{seasonNames[String(season)]}</span>
                )}
              </button>
            ))}
          </div>

          {mixedEpisodes.length > 0 && isAdmin && (
            <div className="mt-5 flex justify-center">
              <button
                onClick={handleDownloadSeason}
                disabled={downloadState.active}
                className="group inline-flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-white font-manrope font-semibold text-sm tracking-wide rounded-sm transition-all disabled:opacity-60 disabled:cursor-wait border border-amber-400/40"
                data-testid="download-season-btn"
                title="Admin only — download season ZIP"
              >
                {downloadState.active ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Packaging {downloadState.progress}/{downloadState.total}...
                  </>
                ) : downloadState.done ? (
                  <>
                    <CheckCircle size={16} />
                    Downloaded!
                  </>
                ) : (
                  <>
                    <Download size={16} className="group-hover:-translate-y-0.5 transition-transform" />
                    Admin · Download Season {selectedSeason} ({mixedEpisodes.length} episodes)
                  </>
                )}
              </button>
            </div>
          )}
        </header>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : episodes.length === 0 ? (
          <div className="text-center py-20">
            <Book size={48} className="mx-auto mb-4 text-gray-700" />
            <p className="text-gray-500">No episodes found for this season</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
            {episodes.map((ep) => (
              <button
                key={ep.id}
                onClick={() => navigate(`/episodes/${ep.id}`)}
                className="group relative overflow-hidden border border-white/10 rounded-lg hover:border-red-500/40 transition-all text-left bg-[#0a0a0a]"
                data-testid={`episode-card-${ep.id}`}
              >
                <div className="h-40 relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                    style={{ backgroundImage: `url(${ep.thumbnail_url ? `${BACKEND_URL}${ep.thumbnail_url}` : COVER_IMAGE})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-xs font-mono text-red-500 backdrop-blur-sm">
                    E{ep.episode_number}
                  </div>
                  {ep.mixed_audio_url && (
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-emerald-600/90 text-[10px] text-white rounded font-mono backdrop-blur-sm">
                      Audio Ready
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-cinzel text-sm font-bold mb-1.5 group-hover:text-red-500 transition-colors line-clamp-2">
                    {ep.title}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{ep.logline}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
